/**
 * usePhase · thin wrapper around useCompanyMode exposing the rollout phase.
 *
 * Phase 1 (Bruno / new dispatchers): dashboard_mode === "audit"
 *   Surfaces only Overview + Compliance & Intelligence + Settings.
 *
 * Phase 2 (test/live): dashboard_mode === "full" | "deals" | "agents" | "custom"
 *   All advanced features (load board, dispatch, fleet, trust links, rate intel).
 *
 * Flipping a tenant from Phase 1 to Phase 2 is a single
 * `companies.settings.dashboard_mode` update · no code deploy.
 *
 * 2026-07-13 fail-closed fix: a MISSING or unknown dashboard_mode now resolves
 * to phase1, not phase2. The choose-role signup flow (SignupRolePicker) wrote
 * only logistics_role, so every self-serve tenant had no dashboard_mode and the
 * old `!== "audit"` check dropped them into phase2 — new broker/carrier signups
 * saw all ~22 Phase-2 surfaces instead of the verification wedge (broker churn
 * wargame wf_67451d11, 2026-07-13). Phase 2 must be an explicit opt-in mode.
 */
import { useCompanyMode } from "@/hooks/useCompanyMode";

export type Phase = "phase1" | "phase2";

/** The only dashboard_mode values that unlock Phase 2. Anything else — including
 * null/undefined (mode never set) — is Phase 1. */
const PHASE2_MODES = new Set(["full", "deals", "agents", "custom"]);

export function usePhase() {
  const { dashboardMode, setDashboardMode, dashboardModeLoaded } = useCompanyMode();
  const phase: Phase =
    dashboardMode != null && PHASE2_MODES.has(dashboardMode) ? "phase2" : "phase1";

  return {
    phase,
    dashboardMode,
    isPhase1: phase === "phase1",
    isPhase2: phase === "phase2",
    setDashboardMode,
    // True until the authoritative phase has been read from the DB. Gates must
    // treat the phase as unresolved while this is true to avoid flashing Phase 2
    // content seeded from stale localStorage. See useCompanyMode.dashboardModeLoaded.
    isLoading: !dashboardModeLoaded,
  };
}
